// ═════════════════════════════════════════════════════════════
// Route prefetch on link intent · touchstart / hover / focus
// ═════════════════════════════════════════════════════════════
// When a user hovers/focuses a nav link OR touches it (mobile), we start
// downloading the destination route's chunk + hero image in the background.
// By the time the click actually lands, the new page paints almost instantly.
//
// This is a lightweight best-effort — safe to call many times; each route
// is prefetched at most once. Ignored on save-data / slow connections
// (respects the user's data preference).

const prefetchedRoutes = new Set();

/**
 * Kicks off a chunk import + hero-image warm-up for a route path.
 * Call it from onMouseEnter / onTouchStart / onFocus on nav links.
 */
export function prefetchRoute(href) {
  if (!href || typeof href !== "string") return;
  if (prefetchedRoutes.has(href)) return;

  // Respect save-data preference
  if (
    typeof navigator !== "undefined" &&
    navigator.connection &&
    (navigator.connection.saveData ||
      /^(slow-2g|2g)$/.test(navigator.connection.effectiveType || ""))
  ) {
    return;
  }

  prefetchedRoutes.add(href);

  // 1) Warm up the route's lazy JS chunk
  //    Map href → dynamic import. Kept as a small table so tree-shaking works.
  try {
    switch (href) {
      case "/projects":
      case "/projects/karls-living-ii":
        import(/* webpackPrefetch: true */ "@/components/ProjectsPage");
        break;
      case "/projects/amselweg-14b":
        import(/* webpackPrefetch: true */ "@/components/AmselwegPage");
        break;
      case "/projects/baumhaselring-180":
        import(/* webpackPrefetch: true */ "@/components/BaumhaselringPage");
        break;
      case "/calculator":
        import(/* webpackPrefetch: true */ "@/components/CalculatorV2");
        break;
      case "/services":
        import(/* webpackPrefetch: true */ "@/components/ServicesPage");
        break;
      case "/team":
        import(/* webpackPrefetch: true */ "@/components/TeamPage");
        break;
      case "/results":
        import(/* webpackPrefetch: true */ "@/components/ResultsPage");
        break;
      case "/process":
        import(/* webpackPrefetch: true */ "@/components/ProcessPage");
        break;
      default:
        break;
    }
  } catch {
    /* Ignore prefetch failures — never break the click */
  }

  // 2) Warm up the destination's LCP hero image via a hidden preload
  const HERO_MAP = {
    "/projects": "/assets/karls/hero-architecture-480w.webp",
    "/projects/karls-living-ii": "/assets/karls/hero-architecture-480w.webp",
    "/projects/amselweg-14b": "/assets/amselweg/img-01-480w.webp",
    "/projects/baumhaselring-180": "/assets/baumhaselring/img-01-480w.webp",
  };
  const heroUrl = HERO_MAP[href];
  if (heroUrl && typeof document !== "undefined") {
    const link = document.createElement("link");
    link.rel = "prefetch";
    link.as = "image";
    link.href = heroUrl;
    document.head.appendChild(link);
  }
}
